import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef, useCallback } from "react";
import React from "react";
import { Button } from "@core/button";
import { Text } from "@core/text";
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Sparkles,
  Target,
  Eye,
  CheckCircle,
  Plus,
  X,
  Check,
} from "lucide-react";

// Import shared components
import BasicInfoStep from "@features/lesson-planner/components/shared/BasicInfoStep";

// Import new UBD-specific step components
import {
  UbdMaarifDataStep,
  UbdGoalsQuestionsStep,
  UbdAssessmentExperiencesStep,
  UbdPlanSummaryStep,
} from "./components";

// Import flow components
import ClassicTeachingFlow from "./components/ClassicTeachingFlow";

// API imports
import {
  useGetUbdPlanner,
  useCreateUbdMaarifLessonPlannerSubsteps,
  useCreateUbdMaarifLessonPlannerSubstepsWithAi,
  useCreateUbdMaarifLessonPlanner,
  useFinalizeUbdMaarifLessonFlow,
} from "@packages/api/madlenAPI";

import {
  UbdMaarifPlannerResponseDto,
  GenerateUbdGoalsRequestDto,
  LearningOutcomeDto,
  GenerateUbdGoalsRequestDtoCourseType,
  GenerateUbdGoalsSubstepRequestDto,
  GenerateUbdGoalsSubstepWithAiRequestDto,
  GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  UbdMaarifPlannerResponseDtoCourseType,
} from "@packages/api/madlenAPI.schemas";
import { getEnvironment } from "@packages/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

// Lesson Flow Template Types
type LessonFlowTemplateType =
  | "klasikOgretim"
  | "oyunlastirilmis"
  | "deneyimOdakli"
  | "isbirlikli";

// AI Modal Types
interface AIOption {
  id: string;
  content: string;
  selected: boolean;
}

interface AIModalState {
  isOpen: boolean;
  type:
    | "understanding"
    | "transfer"
    | "questions"
    | "performanceTasks"
    | "otherEvidence"
    | null;
  title: string;
  options: AIOption[];
  loading: boolean;
}

interface UbdMaarifPlanData {
  // Basic Info
  courseType: GenerateUbdGoalsRequestDtoCourseType | "";
  grade: number;

  // Learning Outcomes
  learningOutcomes: LearningOutcomeDto[];

  // UBD Goals & Questions
  ubdUnderstandingGoals: string[];
  ubdTransferGoals: string[];
  ubdEssentialQuestions: string[];

  // UBD Assessment & Experiences
  ubdPerformanceTasks: string[];
  ubdOtherEvidence: string[];

  // Lesson Flow Template Selection
  selectedTemplate?: LessonFlowTemplateType;

  // Plan ID from backend
  planId?: number;

  // Temporary outputs from AI generation
  tempOutputs?: {
    ubdUnderstandingGoals?: string[];
    ubdTransferGoals?: string[];
    ubdEssentialQuestions?: string[];
    ubdPerformanceTasks?: string[];
    ubdOtherEvidence?: string[];
  };
}

interface LoadingStatesType {
  [key: string]: boolean;
  extraSteps: boolean;
  goalsAndQuestions: boolean;
  assessmentAndExperiences: boolean;
  lessonFlow: boolean;
}

const UbdMaarifLessonPlan = () => {
  const { planId: planIdFromParams } = useParams();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [showLessonFlow, setShowLessonFlow] = useState(false);
  const [lessonFlowData, setLessonFlowData] = useState<any>(null);

  // Use state for planId to ensure it's updated correctly
  const [planId, setPlanId] = useState<number | undefined>(
    planIdFromParams ? parseInt(planIdFromParams) : undefined,
  );

  // Track whether user started with a planId to determine step flow
  const [startedWithPlanId] = useState<boolean>(!!planIdFromParams);

  const [formData, setFormData] = useState<UbdMaarifPlanData>({
    courseType: "",
    grade: 0,
    learningOutcomes: [],
    ubdUnderstandingGoals: [],
    ubdTransferGoals: [],
    ubdEssentialQuestions: [],
    ubdPerformanceTasks: [],
    ubdOtherEvidence: [],
    selectedTemplate: "klasikOgretim",
    tempOutputs: {},
  });

  const [loadingStates, setLoadingStates] = useState<LoadingStatesType>({
    extraSteps: false,
    goalsAndQuestions: false,
    assessmentAndExperiences: false,
    lessonFlow: false,
  });

  // Polling state management
  const [isPolling, setIsPolling] = useState(false);
  const [activePollingType, setActivePollingType] = useState<
    GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType | "LESSON_FLOW" | null
  >(null);

  // AI Modal State
  const [aiModal, setAiModal] = useState<AIModalState>({
    isOpen: false,
    type: null,
    title: "",
    options: [],
    loading: false,
  });

  // API Hooks
  const generateExtraStepsMutation = useCreateUbdMaarifLessonPlanner({
    mutation: {
      onSuccess: (data: UbdMaarifPlannerResponseDto) => {
        console.log("Extra steps generated successfully:", data);

        // Update both the form data and the planId state
        setPlanId(data.id);
        setFormData((prev) => ({
          ...prev,
          planId: data.id,
        }));

        setLoadingStates((prev) => ({ ...prev, extraSteps: false }));

        // Automatically move to next step after successful generation
        setCurrentStep(2); // Move to UBD Goals step
      },
      onError: (error) => {
        console.error("Failed to generate extra steps:", error);
        setLoadingStates((prev) => ({ ...prev, extraSteps: false }));
      },
    },
  });

  const createSubstepsWithAiMutation =
    useCreateUbdMaarifLessonPlannerSubstepsWithAi({
      mutation: {
        onSuccess: (data: UbdMaarifPlannerResponseDto) => {
          console.log("AI substeps generated successfully:", data);
          queryClient.setQueryData(planQueryKey, (old: any) => {
            return {
              ...old,
              tempOutputs: {},
            };
          });
          startPollingForUpdates();
        },
        onError: (error) => {
          console.error("Failed to generate AI substeps:", error);
          setLoadingStates((prev) => ({
            ...prev,
            goalsAndQuestions: false,
            assessmentAndExperiences: false,
          }));

          // Stop polling on error
          stopPolling();
        },
      },
    });

  const createSubstepsMutation = useCreateUbdMaarifLessonPlannerSubsteps({
    mutation: {
      onSuccess: (data: UbdMaarifPlannerResponseDto) => {
        console.log("Manual substeps created successfully:", data);

        setLoadingStates((prev) => ({
          ...prev,
          goalsAndQuestions: false,
          assessmentAndExperiences: false,
        }));
      },
      onError: (error) => {
        console.error("Failed to create manual substeps:", error);
        setLoadingStates((prev) => ({
          ...prev,
          goalsAndQuestions: false,
          assessmentAndExperiences: false,
        }));
      },
    },
  });

  // New API hook for finalizing lesson flow
  const finalizeLessonFlowMutation = useFinalizeUbdMaarifLessonFlow({
    mutation: {
      onSuccess: (data: UbdMaarifPlannerResponseDto) => {
        console.log("Lesson flow finalized successfully:", data);

        // Check if lesson flow is ready
        if (data.lessonFlow) {
          // Lesson flow is ready, save it and show the flow
          setLessonFlowData(data);
          setShowLessonFlow(true);
          setLoadingStates((prev) => ({ ...prev, lessonFlow: false }));

          // Stop polling if it was active
          if (activePollingType === "LESSON_FLOW") {
            stopPolling();
          }
        } else {
          // Lesson flow is still null, start polling
          console.log("Lesson flow is null, starting polling...");
          setActivePollingType("LESSON_FLOW");
          startPollingForUpdates();
        }
      },
      onError: (error) => {
        console.error("Failed to finalize lesson flow:", error);
        setLoadingStates((prev) => ({ ...prev, lessonFlow: false }));

        // Stop polling on error
        if (activePollingType === "LESSON_FLOW") {
          stopPolling();
        }
      },
    },
  });

  const {
    data: existingPlanData,
    refetch: refetchPlanData,
    queryKey: planQueryKey,
    isLoading: isLoadingPlanData,
    isFetching: isFetchingPlanData,
  } = useGetUbdPlanner(planId!, {
    query: {
      // Don't use automatic refetching - we'll control this manually
      enabled: !!planId,
      refetchOnWindowFocus: false,
      refetchOnMount: true, // Initial fetch when component mounts and planId is available
    },
  });
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Log planId for debugging
  useEffect(() => {
    console.log("Current planId:", planId);
  }, [planId]);

  // Load existing plan data ONLY when planId changes or data is first loaded
  useEffect(() => {
    if (
      existingPlanData &&
      existingPlanData.id === planId &&
      formData.courseType === ""
    ) {
      setFormData({
        courseType: existingPlanData.courseType || "",
        grade: existingPlanData.grade || 0,
        learningOutcomes: [],
        ubdUnderstandingGoals: existingPlanData.ubdUnderstandingGoals || [],
        ubdTransferGoals: existingPlanData.ubdTransferGoals || [],
        ubdEssentialQuestions: existingPlanData.ubdEssentialQuestions || [],
        ubdPerformanceTasks: existingPlanData.ubdPerformanceTasks || [],
        ubdOtherEvidence: existingPlanData.ubdOtherEvidence || [],
        selectedTemplate: "klasikOgretim",
        planId: existingPlanData.id,
        tempOutputs: {},
      });

      queryClient.setQueryData(planQueryKey, (old: any) => ({
        ...old,
        tempOutputs: {},
      }));

      // Check if lesson flow already exists
      if (existingPlanData.lessonFlow) {
        setLessonFlowData(existingPlanData);
        setShowLessonFlow(true);
      }
    }
  }, [existingPlanData, planId]);

  // Stop polling
  const stopPolling = useCallback(() => {
    console.log("Stopping polling");

    // Clear the interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Reset state
    setIsPolling(false);
    setActivePollingType(null);
  }, []);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Handle polling updates - separate from initial data load
  useEffect(() => {
    // Skip if not polling or no data
    if (!isPolling || !existingPlanData || !activePollingType) return;

    console.log("Checking if required data has arrived:", {
      activePollingType,
      existingPlanData,
    });

    // Helper function to check if an array has content
    const hasContent = (arr: any[] | undefined) =>
      Array.isArray(arr) && arr.length > 0;

    // Get tempOutputs from the response
    const tempOutputs = (existingPlanData as any).tempOutputs || {};

    // Determine if we should stop polling based on the active polling type
    let shouldStopPolling = false;

    if (activePollingType === "LESSON_FLOW") {
      // Check if lesson flow is ready
      shouldStopPolling =
        existingPlanData.lessonFlow !== null &&
        existingPlanData.lessonFlow !== undefined;

      if (shouldStopPolling) {
        console.log("Lesson flow data received, stopping polling");

        // Save the lesson flow data and show the flow
        setLessonFlowData(existingPlanData);
        setShowLessonFlow(true);
        setLoadingStates((prev) => ({ ...prev, lessonFlow: false }));
      } else {
        console.log("Lesson flow is still null, continuing to poll...");
      }
    } else if (activePollingType === "GOALS_AND_QUESTIONS") {
      // Check if all three types have content in tempOutputs
      shouldStopPolling =
        hasContent(tempOutputs.ubdUnderstandingGoals) &&
        hasContent(tempOutputs.ubdTransferGoals) &&
        hasContent(tempOutputs.ubdEssentialQuestions);

      if (shouldStopPolling) {
        console.log(
          "All three goal types have data in tempOutputs, stopping polling",
        );

        // For "generate all" - replace all fields with tempOutputs (no modal, no appending)
        setFormData((prev) => {
          const newFormData = {
            ...prev,
            ubdUnderstandingGoals: tempOutputs.ubdUnderstandingGoals || [],
            ubdTransferGoals: tempOutputs.ubdTransferGoals || [],
            ubdEssentialQuestions: tempOutputs.ubdEssentialQuestions || [],
          };
          createSubstepsMutation.mutate({
            id: planId!,
            data: {
              userExtraUbdUnderstandingGoals: newFormData.ubdUnderstandingGoals,
              userExtraUbdTransferGoals: newFormData.ubdTransferGoals,
              userExtraUbdEssentialQuestions: newFormData.ubdEssentialQuestions,
            },
          });
          return newFormData;
        });
        setLoadingStates((prev) => ({ ...prev, goalsAndQuestions: false }));
      }
    } else if (activePollingType === "UNDERSTANDING_GOALS") {
      // Check if understanding goals exist in tempOutputs
      shouldStopPolling = hasContent(tempOutputs.ubdUnderstandingGoals);

      if (shouldStopPolling) {
        console.log(
          "Understanding goals data received in tempOutputs, stopping polling",
        );

        // Update form data with tempOutputs
        setFormData((prev) => ({
          ...prev,
          tempOutputs: tempOutputs,
        }));
        setLoadingStates((prev) => ({ ...prev, goalsAndQuestions: false }));
      }
    } else if (activePollingType === "TRANSFER_GOALS") {
      // Check if transfer goals exist in tempOutputs
      shouldStopPolling = hasContent(tempOutputs.ubdTransferGoals);

      if (shouldStopPolling) {
        console.log(
          "Transfer goals data received in tempOutputs, stopping polling",
        );

        // Update form data with tempOutputs
        setFormData((prev) => ({
          ...prev,
          tempOutputs: tempOutputs,
        }));
        setLoadingStates((prev) => ({ ...prev, goalsAndQuestions: false }));
      }
    } else if (activePollingType === "ESSENTIAL_QUESTIONS") {
      // Check if essential questions exist in tempOutputs
      shouldStopPolling = hasContent(tempOutputs.ubdEssentialQuestions);

      if (shouldStopPolling) {
        console.log(
          "Essential questions data received in tempOutputs, stopping polling",
        );

        // Update form data with tempOutputs
        setFormData((prev) => ({
          ...prev,
          tempOutputs: tempOutputs,
        }));
        setLoadingStates((prev) => ({ ...prev, goalsAndQuestions: false }));
      }
    } else if (activePollingType === "ASSESSMENT_AND_EXPERIENCES") {
      // Check if both assessment types exist in tempOutputs
      shouldStopPolling =
        hasContent(tempOutputs.ubdPerformanceTasks) &&
        hasContent(tempOutputs.ubdOtherEvidence);

      if (shouldStopPolling) {
        console.log(
          "Both assessment types received in tempOutputs, stopping polling",
        );

        // For "generate all" - replace all fields with tempOutputs (no modal, no appending)
        setFormData((prev) => ({
          ...prev,
          ubdPerformanceTasks: tempOutputs.ubdPerformanceTasks || [],
          ubdOtherEvidence: tempOutputs.ubdOtherEvidence || [],
        }));
        setLoadingStates((prev) => ({
          ...prev,
          assessmentAndExperiences: false,
        }));
      }
    } else if (activePollingType === "PERFORMANCE_TASKS") {
      // Check if performance tasks exist in tempOutputs
      shouldStopPolling = hasContent(tempOutputs.ubdPerformanceTasks);

      if (shouldStopPolling) {
        console.log(
          "Performance tasks data received in tempOutputs, stopping polling",
        );

        // Update form data with tempOutputs
        setFormData((prev) => ({
          ...prev,
          tempOutputs: tempOutputs,
        }));
        setLoadingStates((prev) => ({
          ...prev,
          assessmentAndExperiences: false,
        }));
      }
    } else if (activePollingType === "OTHER_EVIDENCE") {
      // Check if other evidence exists in tempOutputs
      shouldStopPolling = hasContent(tempOutputs.ubdOtherEvidence);

      if (shouldStopPolling) {
        console.log(
          "Other evidence data received in tempOutputs, stopping polling",
        );

        // Update form data with tempOutputs
        setFormData((prev) => ({
          ...prev,
          tempOutputs: tempOutputs,
        }));
        setLoadingStates((prev) => ({
          ...prev,
          assessmentAndExperiences: false,
        }));
      }
    }

    // If we found the data we need, stop polling
    if (shouldStopPolling) {
      queryClient.removeQueries({ queryKey: planQueryKey });
      queryClient.setQueryData(planQueryKey, (old: any) => {
        return {
          ...old,
          tempOutputs: {},
        };
      });
      stopPolling();
    } else {
      console.log(`Still waiting for ${activePollingType} data...`);
    }
  }, [existingPlanData, isPolling, activePollingType, stopPolling]);

  useEffect(() => {
    const updatedFormData: GenerateUbdGoalsSubstepRequestDto = {};
    if (!aiModal.isOpen) {
      if (
        formData.ubdUnderstandingGoals.length !==
        existingPlanData?.ubdUnderstandingGoals?.length
      ) {
        updatedFormData.userExtraUbdUnderstandingGoals =
          existingPlanData?.ubdUnderstandingGoals || [];
      }
      if (
        formData.ubdTransferGoals.length !==
        existingPlanData?.ubdTransferGoals?.length
      ) {
        updatedFormData.userExtraUbdTransferGoals =
          existingPlanData?.ubdTransferGoals || [];
      }
      if (
        formData.ubdEssentialQuestions.length !==
        existingPlanData?.ubdEssentialQuestions?.length
      ) {
        updatedFormData.userExtraUbdEssentialQuestions =
          existingPlanData?.ubdEssentialQuestions || [];
      }
      if (
        formData.ubdPerformanceTasks.length !==
        existingPlanData?.ubdPerformanceTasks?.length
      ) {
        updatedFormData.userExtraUbdPerformanceTasks =
          existingPlanData?.ubdPerformanceTasks || [];
      }
      if (
        formData.ubdOtherEvidence.length !==
        existingPlanData?.ubdOtherEvidence?.length
      ) {
        updatedFormData.userExtraUbdOtherEvidence =
          existingPlanData?.ubdOtherEvidence || [];
      }
      createSubstepsMutation.mutate({
        id: planId!,
        data: updatedFormData,
      });
    }
  }, [aiModal.isOpen]);

  // Handle tempOutputs when they arrive from AI generation
  useEffect(() => {
    if (!formData.tempOutputs || !aiModal.isOpen) return;

    const hasContent = (arr: any[] | undefined) =>
      Array.isArray(arr) && arr.length > 0;

    let tempData: string[] = [];
    let shouldUpdateModal = false;

    if (
      aiModal.type === "understanding" &&
      hasContent(formData.tempOutputs.ubdUnderstandingGoals)
    ) {
      tempData = formData.tempOutputs.ubdUnderstandingGoals || [];
      shouldUpdateModal = true;
    } else if (
      aiModal.type === "transfer" &&
      hasContent(formData.tempOutputs.ubdTransferGoals)
    ) {
      tempData = formData.tempOutputs.ubdTransferGoals || [];
      shouldUpdateModal = true;
    } else if (
      aiModal.type === "questions" &&
      hasContent(formData.tempOutputs.ubdEssentialQuestions)
    ) {
      tempData = formData.tempOutputs.ubdEssentialQuestions || [];
      shouldUpdateModal = true;
    } else if (
      aiModal.type === "performanceTasks" &&
      hasContent(formData.tempOutputs.ubdPerformanceTasks)
    ) {
      tempData = formData.tempOutputs.ubdPerformanceTasks || [];
      shouldUpdateModal = true;
    } else if (
      aiModal.type === "otherEvidence" &&
      hasContent(formData.tempOutputs.ubdOtherEvidence)
    ) {
      tempData = formData.tempOutputs.ubdOtherEvidence || [];
      shouldUpdateModal = true;
    }

    if (shouldUpdateModal) {
      console.log(`Received tempOutputs for ${aiModal.type}:`, tempData);

      const options = tempData.map((content, index) => ({
        id: generateId(),
        content,
        selected: true, // Auto-select AI generated options
      }));

      setAiModal((prev) => ({
        ...prev,
        options,
        loading: false,
      }));
    }
  }, [formData.tempOutputs, aiModal.isOpen, aiModal.type]);

  const allSteps = [
    {
      name: t("ubdMaarifPlan.steps.basicInfo"),
      component: "basicInfo",
      color: "bg-blue-500",
      icon: <BookOpen className="w-4 h-4" />,
      description: t("ubdMaarifPlan.stepDescriptions.basicInfo"),
    },
    {
      name: t("ubdMaarifPlan.steps.maarifData"),
      component: "maarifData",
      color: "bg-green-500",
      icon: <Target className="w-4 h-4" />,
      description: t("ubdMaarifPlan.stepDescriptions.maarifData"),
    },
    {
      name: t("ubdMaarifPlan.steps.ubdGoals"),
      component: "ubdGoals",
      color: "bg-purple-500",
      icon: <Sparkles className="w-4 h-4" />,
      description: t("ubdMaarifPlan.stepDescriptions.ubdGoals"),
    },
    {
      name: t("ubdMaarifPlan.steps.ubdAssessment"),
      component: "ubdAssessment",
      color: "bg-orange-500",
      icon: <Eye className="w-4 h-4" />,
      description: t("ubdMaarifPlan.stepDescriptions.ubdAssessment"),
    },
    {
      name: t("ubdMaarifPlan.steps.lessonFlowTemplate"),
      component: "lessonFlowTemplate",
      color: "bg-teal-500",
      icon: <CheckCircle className="w-4 h-4" />,
      description: t("ubdMaarifPlan.stepDescriptions.lessonFlowTemplate"),
    },
  ];

  // Filter steps based on whether we started with a planId (skip first two steps if we did)
  const steps = startedWithPlanId ? allSteps.slice(2) : allSteps;

  // Handle step 1 completion - Generate extra steps
  const handleGenerateExtraSteps = () => {
    if (
      !formData.courseType ||
      formData.grade === 0 ||
      formData.learningOutcomes.length === 0
    ) {
      console.error("Missing required data for generating extra steps");
      return;
    }

    setLoadingStates((prev) => ({ ...prev, extraSteps: true }));

    const requestData: GenerateUbdGoalsRequestDto = {
      courseType: formData.courseType as UbdMaarifPlannerResponseDtoCourseType,
      grade: formData.grade,
      environment: getEnvironment(),
      learningOutcomeIds: formData.learningOutcomes
        .map((outcome) => Number(outcome.id))
        .filter(Boolean),
    };

    console.log("Generating extra steps with data:", requestData);
    generateExtraStepsMutation.mutate({ data: requestData });
  };

  // Handle AI generation for goals and questions
  const handleGenerateGoalsWithAI = (
    generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  ) => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    console.log(`Generating ${generationType} with planId:`, planId);

    // Set loading state based on generation type
    if (generationType === "GOALS_AND_QUESTIONS") {
      setLoadingStates((prev) => ({ ...prev, goalsAndQuestions: true }));
    } else if (
      generationType === "UNDERSTANDING_GOALS" ||
      generationType === "TRANSFER_GOALS" ||
      generationType === "ESSENTIAL_QUESTIONS"
    ) {
      setLoadingStates((prev) => ({ ...prev, goalsAndQuestions: true }));
    }

    // Set the active polling type
    setActivePollingType(generationType);

    const requestData: GenerateUbdGoalsSubstepWithAiRequestDto = {
      generationType,
    };

    queryClient.setQueryData(planQueryKey, (old: any) => {
      return {
        ...old,
        tempOutputs: {},
      };
    });

    // Make the API call to generate content
    createSubstepsWithAiMutation.mutate({
      id: planId,
      data: requestData,
    });
  };

  // Handle AI generation for assessment
  const handleGenerateAssessmentWithAI = (
    generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  ) => {
    if (!planId) {
      console.error(
        "Plan ID is required for AI generation. Current planId:",
        planId,
      );
      return;
    }

    console.log(`Generating ${generationType} with planId:`, planId);

    // Set loading state based on generation type
    if (generationType === "ASSESSMENT_AND_EXPERIENCES") {
      setLoadingStates((prev) => ({ ...prev, assessmentAndExperiences: true }));
    } else if (
      generationType === "PERFORMANCE_TASKS" ||
      generationType === "OTHER_EVIDENCE"
    ) {
      setLoadingStates((prev) => ({ ...prev, assessmentAndExperiences: true }));
    }

    // Set the active polling type
    setActivePollingType(generationType);

    const requestData: GenerateUbdGoalsSubstepWithAiRequestDto = {
      generationType,
    };

    // Make the API call to generate content
    createSubstepsWithAiMutation.mutate({
      id: planId,
      data: requestData,
    });
  };

  // Handle manual content submission
  const handleSubmitManualContent = () => {
    if (!planId) {
      console.error(
        "Plan ID is required for manual content submission. Current planId:",
        planId,
      );
      return;
    }

    const requestData: GenerateUbdGoalsSubstepRequestDto = {
      userExtraUbdUnderstandingGoals: formData.ubdUnderstandingGoals,
      userExtraUbdTransferGoals: formData.ubdTransferGoals,
      userExtraUbdEssentialQuestions: formData.ubdEssentialQuestions,
      userExtraUbdPerformanceTasks: formData.ubdPerformanceTasks,
      userExtraUbdOtherEvidence: formData.ubdOtherEvidence,
    };

    console.log("Submitting manual content with planId:", planId);
    createSubstepsMutation.mutate({
      id: planId,
      data: requestData,
    });
  };

  // AI Modal Functions
  const generateId = () => Math.random().toString(36).substr(2, 9);

  const generateAIOptions = (
    type:
      | "understanding"
      | "transfer"
      | "questions"
      | "performanceTasks"
      | "otherEvidence",
    generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  ) => {
    const titles = {
      understanding: "Anlama Hedefi Seçenekleri",
      transfer: "Transfer Hedefi Seçenekleri",
      questions: "Önemli Sorular Seçenekleri",
      performanceTasks: "Performans Görevi Seçenekleri",
      otherEvidence: "Diğer Kanıt Seçenekleri",
    };

    setAiModal({
      isOpen: true,
      type,
      title: titles[type],
      options: [],
      loading: true,
    });

    // Call the appropriate AI generation function
    if (
      type === "understanding" ||
      type === "transfer" ||
      type === "questions"
    ) {
      handleGenerateGoalsWithAI(generationType);
    } else if (type === "performanceTasks" || type === "otherEvidence") {
      handleGenerateAssessmentWithAI(generationType);
    }
  };

  const toggleOptionSelection = (optionId: string) => {
    setAiModal((prev) => ({
      ...prev,
      options: prev.options.map((option) =>
        option.id === optionId
          ? { ...option, selected: !option.selected }
          : option,
      ),
    }));
  };

  const updateOptionContent = (optionId: string, content: string) => {
    setAiModal((prev) => ({
      ...prev,
      options: prev.options.map((option) =>
        option.id === optionId ? { ...option, content } : option,
      ),
    }));
  };

  const addSelectedOptionsToCards = () => {
    const selectedOptions = aiModal.options.filter(
      (option) => option.selected && option.content.trim(),
    );

    if (selectedOptions.length === 0) return;

    const newItems = selectedOptions.map((option) => option.content);

    if (aiModal.type === "understanding") {
      setFormData((prev) => {
        const existingContent = prev.ubdUnderstandingGoals.filter(
          (goal) => goal.trim() !== "",
        );
        const updated = [...existingContent, ...newItems];
        return { ...prev, ubdUnderstandingGoals: updated };
      });
    } else if (aiModal.type === "transfer") {
      setFormData((prev) => {
        const existingContent = prev.ubdTransferGoals.filter(
          (goal) => goal.trim() !== "",
        );
        const updated = [...existingContent, ...newItems];
        return { ...prev, ubdTransferGoals: updated };
      });
    } else if (aiModal.type === "questions") {
      setFormData((prev) => {
        const existingContent = prev.ubdEssentialQuestions.filter(
          (question) => question.trim() !== "",
        );
        const updated = [...existingContent, ...newItems];
        return { ...prev, ubdEssentialQuestions: updated };
      });
    } else if (aiModal.type === "performanceTasks") {
      setFormData((prev) => {
        const existingContent = prev.ubdPerformanceTasks.filter(
          (task) => task.trim() !== "",
        );
        const updated = [...existingContent, ...newItems];
        return { ...prev, ubdPerformanceTasks: updated };
      });
    } else if (aiModal.type === "otherEvidence") {
      setFormData((prev) => {
        const existingContent = prev.ubdOtherEvidence.filter(
          (evidence) => evidence.trim() !== "",
        );
        const updated = [...existingContent, ...newItems];
        return { ...prev, ubdOtherEvidence: updated };
      });
    }

    // Clear temp outputs and close modal
    setFormData((prev) => ({ ...prev, tempOutputs: {} }));
    setAiModal({
      isOpen: false,
      type: null,
      title: "",
      options: [],
      loading: false,
    });
  };

  const closeModal = () => {
    setFormData((prev) => ({ ...prev, tempOutputs: {} }));
    setAiModal({
      isOpen: false,
      type: null,
      title: "",
      options: [],
      loading: false,
    });
  };

  // Start polling for updates from the API
  const startPollingForUpdates = () => {
    console.log("Starting polling for updates...");

    // Set polling state
    setIsPolling(true);

    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Set up interval for polling
    pollingIntervalRef.current = setInterval(() => {
      console.log("Polling: Fetching data...");
      refetchPlanData();
    }, 2000);

    // Initial fetch immediately
    refetchPlanData();
  };

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      // Cleanup function
      stopPolling();
    };
  }, [stopPolling]);

  // Handle lesson flow creation with finalizeUbdMaarifLessonFlow API
  const handleCreateLessonFlow = (templateType: LessonFlowTemplateType) => {
    if (!planId) {
      console.error("Plan ID is required for lesson flow creation");
      return;
    }

    console.log(
      `Creating lesson flow with template: ${templateType} and planId: ${planId}`,
    );

    setLoadingStates((prev) => ({ ...prev, lessonFlow: true }));

    // Update selected template in form data
    setFormData((prev) => ({
      ...prev,
      selectedTemplate: templateType,
    }));

    // Call the finalizeUbdMaarifLessonFlow API
    finalizeLessonFlowMutation.mutate({ id: planId });
  };

  // Handle lesson flow data received
  const handleLessonFlowCreated = (lessonFlowData: any) => {
    console.log("Lesson flow created:", lessonFlowData);
    setLessonFlowData(lessonFlowData);
    setShowLessonFlow(true);
  };

  // Handle back from lesson flow
  const handleBackFromLessonFlow = () => {
    setShowLessonFlow(false);
    setLessonFlowData(null);
  };

  // Render lesson flow component based on template type
  const renderLessonFlow = () => {
    if (!lessonFlowData?.lessonFlow) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="text-gray-400 text-6xl mb-4">❌</div>
            <Text
              variant="h3"
              className="text-xl font-bold text-gray-600 dark:text-gray-400 mb-2"
            >
              Ders Akışı Verisi Bulunamadı
            </Text>
            <Text variant="p" className="text-gray-500 dark:text-gray-400">
              Lesson flow verisi eksik veya bozuk.
            </Text>
            <Button onClick={handleBackFromLessonFlow} variant="outline">
              Geri Dön
            </Button>
          </div>
        </div>
      );
    }

    const templateTipi = lessonFlowData.lessonFlow.templateTipi;

    const flowProps = {
      lessonData: lessonFlowData,
      isLoading: false,
      onBack: handleBackFromLessonFlow,
    };

    console.log("Rendering lesson flow with template type:", templateTipi);
    return <ClassicTeachingFlow {...flowProps} />; // Default fallback
  };

  // If showing lesson flow, render that instead
  if (showLessonFlow) {
    return renderLessonFlow();
  }

  const renderStepContent = () => {
    // Map current step to actual step index based on whether we started with planId
    const actualStepIndex = startedWithPlanId ? currentStep + 2 : currentStep;

    switch (actualStepIndex) {
      case 0:
        return (
          <BasicInfoStep
            isMaarif={true}
            courseType={formData.courseType as string}
            grade={formData.grade}
            onLessonChange={(value: string) =>
              setFormData((prev) => ({
                ...prev,
                courseType: value as GenerateUbdGoalsRequestDtoCourseType,
              }))
            }
            onGradeLevelChange={(value: number) =>
              setFormData((prev) => ({
                ...prev,
                grade: value,
              }))
            }
            selectedOutcomes={formData.learningOutcomes.map((outcome) => ({
              id: outcome.id?.toString() || "",
              text: outcome.text || "",
            }))}
            onOutcomesChange={(outcomes: { id: string; text: string }[]) =>
              setFormData((prev) => ({
                ...prev,
                learningOutcomes: outcomes.map(
                  (outcome) =>
                    ({
                      id: outcome.id,
                      text: outcome.text,
                    }) as LearningOutcomeDto,
                ),
              }))
            }
          />
        );
      case 1:
        return (
          <UbdMaarifDataStep
            gradeLevel={formData.grade}
            lesson={formData.courseType as string}
            selectedOutcomes={formData.learningOutcomes.map((outcome) => ({
              id: outcome.id?.toString() || "",
              text: outcome.text || "",
            }))}
          />
        );
      case 2:
        return (
          <UbdGoalsQuestionsStep
            planId={planId}
            ubdUnderstandingGoals={formData.ubdUnderstandingGoals}
            ubdTransferGoals={formData.ubdTransferGoals}
            ubdEssentialQuestions={formData.ubdEssentialQuestions}
            isLoading={loadingStates.goalsAndQuestions}
            onGenerateWithAI={(
              generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
            ) => {
              if (generationType === "UNDERSTANDING_GOALS") {
                generateAIOptions("understanding", generationType);
              } else if (generationType === "TRANSFER_GOALS") {
                generateAIOptions("transfer", generationType);
              } else if (generationType === "ESSENTIAL_QUESTIONS") {
                generateAIOptions("questions", generationType);
              } else {
                handleGenerateGoalsWithAI(generationType);
              }
            }}
            onFieldChange={(field: string, value: string[] | {}) =>
              setFormData((prev) => ({ ...prev, [field]: value }))
            }
          />
        );
      case 3:
        return (
          <UbdAssessmentExperiencesStep
            planId={planId}
            ubdPerformanceTasks={formData.ubdPerformanceTasks}
            ubdOtherEvidence={formData.ubdOtherEvidence}
            isLoading={loadingStates.assessmentAndExperiences}
            onGenerateWithAI={(
              generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
            ) => {
              if (generationType === "PERFORMANCE_TASKS") {
                generateAIOptions("performanceTasks", generationType);
              } else if (generationType === "OTHER_EVIDENCE") {
                generateAIOptions("otherEvidence", generationType);
              } else {
                handleGenerateAssessmentWithAI(generationType);
              }
            }}
            onFieldChange={(field: string, value: string[]) =>
              setFormData((prev) => ({ ...prev, [field]: value }))
            }
          />
        );
      case 4:
        return (
          <UbdPlanSummaryStep
            formData={{ ...formData, planId }}
            isLoading={loadingStates.lessonFlow}
            onCreateLessonFlow={() =>
              handleCreateLessonFlow(
                formData.selectedTemplate || "klasikOgretim",
              )
            }
          />
        );
      default:
        return null;
    }
  };

  const isStepValid = () => {
    // Map current step to actual step index based on whether we started with planId
    const actualStepIndex = startedWithPlanId ? currentStep + 2 : currentStep;

    switch (actualStepIndex) {
      case 0:
        return (
          formData.courseType !== "" &&
          formData.grade !== 0 &&
          formData.learningOutcomes.length > 0
        );
      case 1:
        return true;
      case 2:
        return true; // UBD goals are optional initially
      case 3:
        return true; // Assessment & experiences are optional initially
      case 4:
        return true; // Summary is always valid
      default:
        return false;
    }
  };

  const handleNextStep = async () => {
    console.log("handleNextStep", currentStep, planId);

    // Map current step to actual step index based on whether we started with planId
    const actualStepIndex = startedWithPlanId ? currentStep + 2 : currentStep;

    if (actualStepIndex === 1 && !planId) {
      await handleGenerateExtraSteps();
      return;
    }

    if (actualStepIndex === 2 && planId) {
      await handleSubmitManualContent();
    }

    if (actualStepIndex === 3 && planId) {
      await handleSubmitManualContent();
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepProgress = () => ((currentStep + 1) / steps.length) * 100;

  const hasContent =
    formData.courseType !== "" ||
    formData.grade > 0 ||
    formData.learningOutcomes.length > 0 ||
    formData.ubdUnderstandingGoals.length > 0 ||
    formData.ubdTransferGoals.length > 0 ||
    formData.ubdEssentialQuestions.length > 0 ||
    formData.ubdPerformanceTasks.length > 0 ||
    formData.ubdOtherEvidence.length > 0;

  // Show loading state when fetching plan data from URL
  if (planIdFromParams && isLoadingPlanData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl p-8">
          <div className="text-center space-y-6">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-blue-600 animate-pulse" />
              </div>
            </div>
            <div className="space-y-2">
              <Text
                variant="h3"
                className="text-xl font-bold text-gray-900 dark:text-gray-100"
              >
                {t("ubdMaarifPlan.loading.fetchingPlan")}
              </Text>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t("ubdMaarifPlan.loading.pleaseWait")}
              </Text>
            </div>
            <div className="flex space-x-1 justify-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-4 px-4">
      <div className="">
        {/* Progress Steps */}
        <div className="relative mb-4">
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-xl p-4 border border-white/20 dark:border-gray-700/30 shadow-lg">
            {/* Progress Info Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-sm">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <Text
                    variant="h3"
                    className="text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    {steps[currentStep]?.name}
                  </Text>
                  <Text
                    variant="p"
                    size="sm"
                    className="text-gray-500 dark:text-gray-400"
                  >
                    {steps[currentStep]?.description}
                  </Text>
                </div>
              </div>
              <div className="text-right">
                <Text
                  variant="p"
                  size="sm"
                  className="text-gray-600 dark:text-gray-400"
                >
                  {t("ubdMaarifPlan.progress.step", {
                    current: currentStep + 1,
                    total: steps.length,
                  })}
                </Text>
                <Text
                  variant="p"
                  size="xs"
                  className="text-gray-500 dark:text-gray-400"
                >
                  {t("ubdMaarifPlan.progress.completed", {
                    percentage: Math.round(getStepProgress()),
                  })}
                </Text>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${getStepProgress()}%` }}
              ></div>
            </div>

            {/* Step Icons */}
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center group cursor-pointer"
                  onClick={() => index <= currentStep && setCurrentStep(index)}
                >
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-lg border-2 transition-all duration-300 ${
                      index === currentStep
                        ? "bg-blue-600 border-blue-700 text-white shadow-md scale-110"
                        : index < currentStep
                          ? "bg-green-500 border-green-500 text-white shadow-sm"
                          : "bg-gray-100 border-gray-300 text-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-500 group-hover:border-blue-300"
                    }`}
                  >
                    {index < currentStep ? (
                      <CheckCircle className="w-4 h-4 animate-in zoom-in duration-300" />
                    ) : (
                      React.cloneElement(step.icon, { className: "w-3 h-3" })
                    )}
                  </div>
                  <Text
                    variant="span"
                    size="xs"
                    className={`mt-1 flex h-12 w-24 items-center justify-center text-center leading-tight transition-colors duration-300 ${
                      index === currentStep
                        ? "text-blue-700 dark:text-blue-300 font-semibold"
                        : index < currentStep
                          ? "text-green-700 dark:text-green-300"
                          : "text-gray-500 dark:text-gray-400 group-hover:text-blue-600"
                    }`}
                  >
                    {step.name}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="relative mb-4 z-10">
          <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl">
            <div className="p-3">
              <div className="relative z-20">{renderStepContent()}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-xl p-4 border border-white/20 dark:border-gray-700/30 shadow-lg">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handlePrevStep}
              disabled={currentStep === 0}
              className="px-6 py-2 rounded-lg border border-gray-300 hover:border-gray-400 dark:border-gray-600 transition-all duration-300 disabled:opacity-50"
            >
              <ChevronLeft size={16} className="mr-2" />
              {t("common.previous")}
            </Button>

            <div className="text-center">
              <Text
                variant="p"
                size="sm"
                className="text-gray-600 dark:text-gray-400"
              >
                {steps[currentStep]?.name}
              </Text>
            </div>

            {!(currentStep === steps.length - 1 && hasContent) && (
              <Button
                variant="solid"
                onClick={handleNextStep}
                disabled={!isStepValid() || loadingStates.extraSteps}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50"
              >
                {loadingStates.extraSteps ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Oluşturuluyor...
                  </>
                ) : currentStep === steps.length - 1 ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    {t("common.complete")}
                  </>
                ) : (
                  <>
                    {t("common.next")}
                    <ChevronRight size={16} className="ml-2" />
                  </>
                )}
              </Button>
            )}

            {currentStep === steps.length - 1 && hasContent && (
              <div className="px-6 py-2 opacity-0"></div>
            )}
          </div>
        </div>
      </div>

      {/* AI Options Modal */}
      {aiModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full h-[92vh] flex flex-col">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-purple-500 via-indigo-600 to-blue-600 p-4 flex-shrink-0 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <Text variant="h3" className="text-lg font-bold text-white">
                      {aiModal.title}
                    </Text>
                    <Text className="text-white/80 text-xs mt-0.5">
                      AI seçeneklerini inceleyin ve düzenleyin
                    </Text>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="flex items-center justify-end mr-4 flex-shrink-0 space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAiModal((prev) => ({
                          ...prev,
                          options: prev.options.map((opt) => ({
                            ...opt,
                            selected: true,
                          })),
                        }));
                      }}
                      className="text-xs px-2 py-1 h-7 text-white"
                    >
                      Tümünü Seç
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAiModal((prev) => ({
                          ...prev,
                          options: prev.options.map((opt) => ({
                            ...opt,
                            selected: false,
                          })),
                        }));
                      }}
                      className="text-xs px-2 py-1 h-7 text-white"
                    >
                      Tümünü Kaldır
                    </Button>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors duration-200"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>

              {/* Progress indicator for loading */}
              {aiModal.loading && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 overflow-hidden">
                  <div className="h-full bg-white/60 animate-pulse"></div>
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="flex-1 flex flex-col min-h-0">
              {aiModal.loading ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 space-y-4">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
                    </div>
                  </div>
                  <div className="text-center space-y-1">
                    <Text className="text-base font-semibold text-gray-700 dark:text-gray-300">
                      AI seçenekleri oluşturuluyor...
                    </Text>
                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                      Bu işlem birkaç saniye sürebilir
                    </Text>
                  </div>
                  <div className="flex space-x-1 justify-center">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"></div>
                    <div
                      className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                </div>
              ) : (
                <>
                  {aiModal.options.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-12 space-y-4">
                      <div className="text-6xl opacity-50">🤖</div>
                      <div className="text-center space-y-2">
                        <Text className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Seçenekler henüz hazır değil
                        </Text>
                        <Text className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                          AI seçenekleri oluşturulurken bir sorun oluştu. Lütfen
                          tekrar deneyin.
                        </Text>
                      </div>
                      <Button
                        variant="outline"
                        onClick={closeModal}
                        className="mt-2"
                      >
                        Kapat
                      </Button>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col px-4 min-h-0">
                      {/* Options List */}
                      <div className="flex-1 overflow-y-auto space-y-2.5 pr-6 min-h-0">
                        {aiModal.options.map((option, index) => (
                          <div
                            key={option.id}
                            onClick={(e) => {
                              // Only toggle if the click is not on an interactive element
                              const target = e.target as HTMLElement;
                              if (
                                !target.closest("textarea") &&
                                !target.closest("button") &&
                                !target.closest("[data-interactive]")
                              ) {
                                toggleOptionSelection(option.id);
                              }
                            }}
                            className={`group mt-2 relative px-3 pt-3 pb-1 border-2 rounded-xl transition-all duration-200 cursor-pointer ${
                              option.selected
                                ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md"
                                : "border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 hover:shadow-sm"
                            }`}
                          >
                            {/* Option Header */}
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 pt-0.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleOptionSelection(option.id);
                                  }}
                                  className={`relative w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                                    option.selected
                                      ? "border-purple-500 bg-purple-500 text-white shadow-sm"
                                      : "border-gray-300 dark:border-gray-500 hover:border-purple-400 dark:hover:border-purple-400"
                                  }`}
                                >
                                  {option.selected && (
                                    <Check className="w-3 h-3" />
                                  )}
                                </button>
                              </div>

                              <div className="flex-1">
                                {/* Option Number */}
                                <div className="flex items-center space-x-2 mb-2">
                                  <div className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-400">
                                    Seçenek {index + 1}
                                  </div>
                                  {option.selected && (
                                    <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900 rounded text-xs font-medium text-green-700 dark:text-green-300">
                                      ✓ Seçili
                                    </div>
                                  )}
                                </div>

                                {/* Content Editor */}
                                <div className="relative">
                                  <textarea
                                    value={option.content}
                                    onChange={(e) =>
                                      updateOptionContent(
                                        option.id,
                                        e.target.value,
                                      )
                                    }
                                    className={`w-full bg-white dark:bg-gray-800 border rounded-lg p-3 text-sm text-gray-800 dark:text-gray-200 resize-none focus:outline-none transition-all duration-200 ${
                                      option.selected
                                        ? "border-purple-300 dark:border-purple-600 focus:border-purple-500 dark:focus:border-purple-400"
                                        : "border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500"
                                    }`}
                                    rows={2}
                                    placeholder="Seçenek içeriğini düzenleyin..."
                                    style={{ minHeight: "3.5rem" }}
                                  />
                                  <div className="absolute bottom-3 right-2 text-xs text-gray-400 pointer-events-none">
                                    {option.content.length}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Hover Actions */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleOptionSelection(option.id);
                                }}
                                className="p-1 bg-white dark:bg-gray-700 rounded-md shadow-md hover:shadow-lg transition-all duration-200"
                                title={
                                  option.selected ? "Seçimi kaldır" : "Seç"
                                }
                              >
                                {option.selected ? (
                                  <X className="w-3 h-3 text-red-500" />
                                ) : (
                                  <Plus className="w-3 h-3 text-green-500" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Modal Footer */}
              {!aiModal.loading && aiModal.options.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-2 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center">
                          <Check className="w-2 h-2 text-white" />
                        </div>
                        <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {
                            aiModal.options.filter(
                              (opt) => opt.selected && opt.content.trim(),
                            ).length
                          }{" "}
                          seçenek hazır
                        </Text>
                      </div>
                      <div className="h-3 w-px bg-gray-300 dark:bg-gray-600"></div>
                      <Text className="text-xs text-gray-500 dark:text-gray-400">
                        Planınıza eklenecek
                      </Text>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={closeModal}
                        className="px-4 py-2 rounded-lg text-sm"
                      >
                        İptal
                      </Button>
                      <Button
                        variant="solid"
                        onClick={addSelectedOptionsToCards}
                        disabled={
                          aiModal.options.filter(
                            (opt) => opt.selected && opt.content.trim(),
                          ).length === 0
                        }
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1.5" />
                        Seçilenleri Ekle
                        {aiModal.options.filter(
                          (opt) => opt.selected && opt.content.trim(),
                        ).length > 0 && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                            {
                              aiModal.options.filter(
                                (opt) => opt.selected && opt.content.trim(),
                              ).length
                            }
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UbdMaarifLessonPlan;
